import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { UIButton, UISubNav, useUINav } from '@/ui';
import { Navigate, useNavigate } from 'react-router';
import { useCompanyContext } from '@/companies/providers';
import { useFetchTeamCounts } from '@/companies/queries';
import { useCompanyPermissions } from '@/companies/hooks';
import { usePagesTranslation } from '../hooks';
import { TeamsList, LimitedViewBanner } from '@/companies/components';
import { CreateTeamModal } from '@/companies/components/create-team-modal';

export function TeamsSettingsPage(): ReactElement {
    const { t } = usePagesTranslation();
    const { setParts } = useUINav();
    useEffect(() => {
        setParts([
            {
                text: t('shared.labels.settings')
            },
            { text: t('shared.labels.teams') }
        ]);
    }, [setParts, t]);

    const { company } = useCompanyContext();
    const { counts } = useFetchTeamCounts(company.id);
    const { canViewTeams, canCreateTeams, canEditTeams } =
        useCompanyPermissions();
    const [isCreateTeamModalOpen, setIsCreateTeamModalOpen] = useState(false);
    const navigate = useNavigate();

    const subtitle = useMemo(() => {
        // Nothing to say until the counts land — better than flashing
        // "0 teams".
        if (!counts) {
            return undefined;
        }

        return `${counts.total} ${counts.total === 1 ? 'team' : 'teams'}`;
    }, [counts]);

    const rightNode = useCallback(() => {
        if (!canCreateTeams) {
            return undefined;
        }
        return (
            <UIButton
                variant="primary"
                text={t('pages.teams_settings.create')}
                leftIcon={'plus-circle'}
                onClick={() => setIsCreateTeamModalOpen(true)}
            />
        );
    }, [canCreateTeams, t]);

    if (!canViewTeams) {
        return <Navigate to="/" replace />;
    }

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    padding: 32,
                    gap: 24
                }}
            >
                {!canEditTeams && <LimitedViewBanner />}
                <UISubNav
                    title={t('shared.labels.teams')}
                    subTitle={subtitle}
                    rightItems={rightNode()}
                />
                <TeamsList
                    companyId={company.id}
                    onTeamPress={(team) => {
                        void navigate(
                            `/company/${company.id}/settings/teams/${team.id}`
                        );
                    }}
                    onTeamMembersPress={(team) => {
                        void navigate(
                            `/company/${company.id}/settings/teams/${team.id}/members`
                        );
                    }}
                />
            </div>
            <CreateTeamModal
                isOpen={isCreateTeamModalOpen}
                onClose={() => setIsCreateTeamModalOpen(false)}
            />
        </>
    );
}
